import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, Input, Select } from '../../components/ui'
import { Footer } from '../../components/Footer'
import { GhostMark } from '../../components/GhostMark'
import { PasskeyButton } from '../../components/PasskeyButton'
import { GoogleGlyph, MicrosoftGlyph, IconCheck } from '../../components/icons'
import { useStore } from '../../lib/store'
import { checkWorkEmail, companyFromDomain } from '../../lib/email'
import { RECRUITER_ROLES } from '../../lib/constants'
import './auth.css'

// Registration is intentionally tiny: work email → auto-detected company →
// role → password → done. The company is inferred from the email domain.
// PocketBase auth always needs a password, so accounts are created with one; the
// "add a passkey" path simply chains enrolment onto that same create so you skip
// the trip to Settings. SSO is a later pass.
const SOON = 'SSO is coming soon — create your account with a password for now.'

export function Register() {
  const { register, enrollPasskey } = useStore()
  const nav = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [touched, setTouched] = useState(false)
  const [role, setRole] = useState(RECRUITER_ROLES[0])
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)

  const check = useMemo(() => checkWorkEmail(email), [email])
  const company = check.valid ? companyFromDomain(check.domain) : ''
  // Only surface the email error once the user has engaged with a non-empty value.
  const showError = touched && email.trim().length > 0 && !check.valid

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    setError('')
    setNotice('')
    if (!check.valid) return
    if (password.length < 8) {
      setError('Use at least 8 characters for your password.')
      return
    }
    setLoading(true)
    try {
      await register({ email, password, role })
      nav('/candidates')
    } catch (err) {
      setLoading(false)
      const status = (err as { status?: number })?.status
      setError(
        status === 400
          ? 'Could not create the account — that email may already be registered.'
          : 'Couldn’t reach the server. Check your connection and try again.',
      )
    }
  }

  // Same create as the form, then chain passkey enrolment so the new account is
  // passwordless from the first sign-in. A password is still required (PocketBase
  // auth needs one); this just saves a trip to Settings. Throwing keeps the
  // PasskeyButton from navigating on a validation/enrolment failure — once
  // register() authenticates, the route guard redirects to the console anyway.
  async function registerWithPasskey() {
    setTouched(true)
    setNotice('')
    if (!check.valid) throw new Error('Enter your work email above first.')
    if (password.length < 8) throw new Error('Choose a password of at least 8 characters above.')
    await register({ email, password, role })
    await enrollPasskey()
  }

  return (
    <div className="auth">
      <Card className="auth__card">
        <div className="auth__logo">
          <GhostMark size={64} glow breathing />
        </div>
        <h1 className="auth__title">Get verified candidates</h1>
        <p className="auth__sub">Create your recruiter account in seconds.</p>

        <form className="auth__form" onSubmit={onSubmit} noValidate>
          <Input
            id="reg-email"
            label="Work email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            error={showError ? check.reason : undefined}
            ok={check.valid}
            hint={!touched ? 'Use your company domain — not a personal inbox.' : undefined}
          />

          {check.valid && (
            <div className="company-chip">
              <span className="company-chip__icon">{company.slice(0, 1)}</span>
              <span className="company-chip__text">
                <b>{company}</b>
                <small>Detected from {check.domain}</small>
              </span>
              <span style={{ marginLeft: 'auto', color: 'var(--cred-live)', display: 'flex' }}>
                <IconCheck size={16} />
              </span>
            </div>
          )}

          <Select
            id="reg-role"
            label="Your role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            options={RECRUITER_ROLES.map((r) => ({ value: r, label: r }))}
          />

          <Input
            id="reg-password"
            label="Password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              if (error) setError('')
            }}
            error={error || undefined}
          />

          <Button type="submit" variant="primary" size="lg" block loading={loading} disabled={!check.valid}>
            Create account
          </Button>

          <div className="auth__divider">or</div>

          {/* Create the account, then enrol a passkey in one step. */}
          <PasskeyButton
            label="Create account & add a passkey"
            onActivate={registerWithPasskey}
            onDone={() => nav('/candidates')}
          />

          <div className="auth__sso">
            <button type="button" className="sso-btn sso-btn--secondary" onClick={() => setNotice(SOON)}>
              <GoogleGlyph /> Sign up with Google
            </button>
            <button type="button" className="sso-btn sso-btn--secondary" onClick={() => setNotice(SOON)}>
              <MicrosoftGlyph /> Sign up with Microsoft
            </button>
          </div>

          {notice && <p className="auth__note">{notice}</p>}
          <p className="auth__note">No credit card. Free tier includes 5 unlocks a month.</p>
        </form>

        <p className="auth__foot">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </Card>
      <Footer />
    </div>
  )
}
